import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiChallengeGame {
  gameId: string;
  name: { en: string; ar: string };
  gameUrl?: string;
  image?: string;
  description?: { en: string; ar: string };
}

export interface ApiChallengeReward {
  position: number;
  rewardId?: string | null;
  type: string;
  priceName?: { en: string; ar: string };
  cash?: number | null;
  currency?: string | null;
  image?: string;
}

export interface ApiChallengeCountry {
  countryId?: string;
  countryCode?: string;
  name?: string;
}

export type ChallengeStatus = 'upcoming' | 'ongoing' | 'ended';

export interface ApiChallenge {
  _id: string;
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  games: ApiChallengeGame[];
  rewards: ApiChallengeReward[];
  countries: ApiChallengeCountry[];
  bannerEn: string;
  bannerAr: string;
  startDate: string | null;
  endDate: string | null;
  status: ChallengeStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  gamesCount?: number;
  rewardsCount?: number;
  participants?: number;
}

export interface ChallengeListFilters {
  search?: string;
  status?: 'upcoming' | 'ongoing' | 'ended';
  isActive?: boolean;
}

export interface ChallengeListResponse {
  success: boolean;
  code?: number;
  data: { list: ApiChallenge[]; total: number; page: number; limit: number; totalPages: number };
}

export interface ChallengeGetResponse {
  success: boolean;
  code?: number;
  data: ApiChallenge;
}

export interface ChallengeMutationResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: ApiChallenge;
}

export interface ChallengeToggleResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: { isActive: boolean };
}

export interface ChallengeLeaderboardEntry {
  rank: number;
  userId?: string;
  player: string;
  avatar?: string;
  countryId?: string;
  email?: string;
  phoneCode?: string;
  phone?: string;
  score: number;
}

export interface ChallengeLeaderboardResponse {
  success: boolean;
  code?: number;
  data: { list: ChallengeLeaderboardEntry[]; total: number; page: number; limit: number; totalPages: number };
}

export interface ChallengeLeaderboardResetResponse {
  success: boolean;
  code?: number;
  message?: string;
}

export interface ChallengeRewardPayload {
  position: number;
  type: string;
  rewardId?: string;
  priceNameEn?: string;
  priceNameAr?: string;
  cash?: number;
  currency?: string;
}

export interface ChallengeGamePayload {
  gameId: string;
}

export interface CreateChallengePayload {
  titleEn: string;
  titleAr: string;
  descriptionEn: string;
  descriptionAr: string;
  startDate: string;
  endDate: string;
  games: ChallengeGamePayload[];
  rewards: ChallengeRewardPayload[];
  countries?: ApiChallengeCountry[];
  isActive?: boolean;
  bannerEn?: File | null;
  bannerAr?: File | null;
}

export interface UpdateChallengePayload extends CreateChallengePayload {
  id: string;
}

const BASE = `${environment.apiUrl}/api/admin/challenges`;

@Injectable({ providedIn: 'root' })
export class ChallengeService {
  private http = inject(HttpClient);

  list(page = 1, limit = 10, filters: ChallengeListFilters = {}): Observable<ChallengeListResponse> {
    return this.http.post<ChallengeListResponse>(`${BASE}/list`, { page, limit, ...filters });
  }

  get(id: string): Observable<ChallengeGetResponse> {
    return this.http.post<ChallengeGetResponse>(`${BASE}/get`, { id });
  }

  create(payload: CreateChallengePayload): Observable<ChallengeMutationResponse> {
    return this.http.post<ChallengeMutationResponse>(`${BASE}/create`, this.toFormData(payload));
  }

  update(payload: UpdateChallengePayload): Observable<ChallengeMutationResponse> {
    const form = this.toFormData(payload);
    form.append('id', payload.id);
    return this.http.post<ChallengeMutationResponse>(`${BASE}/update`, form);
  }

  toggle(id: string): Observable<ChallengeToggleResponse> {
    return this.http.post<ChallengeToggleResponse>(`${BASE}/toggle`, { id });
  }

  leaderboard(id: string, page = 1, limit = 10): Observable<ChallengeLeaderboardResponse> {
    return this.http.post<ChallengeLeaderboardResponse>(`${BASE}/leaderboard`, { id, page, limit });
  }

  resetLeaderboard(id: string): Observable<ChallengeLeaderboardResetResponse> {
    return this.http.post<ChallengeLeaderboardResetResponse>(`${BASE}/leaderboard/reset`, { id });
  }

  imageUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${environment.apiUrl}/${path.replace(/^\//, '')}`;
  }

  private toFormData(payload: CreateChallengePayload): FormData {
    const form = new FormData();
    form.append('title', JSON.stringify({ en: payload.titleEn, ar: payload.titleAr }));
    form.append('description', JSON.stringify({ en: payload.descriptionEn, ar: payload.descriptionAr }));
    form.append('startDate', payload.startDate);
    form.append('endDate', payload.endDate);
    form.append('games', JSON.stringify(payload.games));
    form.append('rewards', JSON.stringify(payload.rewards.map(r => ({
      position: r.position,
      type: r.type,
      rewardId: r.rewardId,
      priceName: { en: r.priceNameEn ?? '', ar: r.priceNameAr ?? '' },
      cash: r.cash,
      currency: r.currency,
    }))));
    form.append('countries', JSON.stringify(payload.countries ?? []));
    form.append('isActive', String(payload.isActive ?? true));
    if (payload.bannerEn) form.append('bannerEn', payload.bannerEn);
    if (payload.bannerAr) form.append('bannerAr', payload.bannerAr);
    return form;
  }
}
